import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaLinkedin } from "react-icons/fa";
import { HiArrowRight } from "react-icons/hi";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslateItems } from "../../hooks/useTranslate";

const UI_TEXT = {
  EN: {
    label:       "About Us",
    headline:    "We want to build a world where organizations are run with social responsibility and driven by purpose & impact",
    stat1l: "Farmers Impacted", stat2l: "Districts/Cities", stat3l: "Beneficiaries",
    eyebrow:     "The People Behind the Mission",
    title:       "Our Team",
    subtitle:    "Led by experienced individuals fully committed to food security and empowerment of the Acehnese community.",
    viewProfile: "View Profile",
    cta:         "Want to join us?",
    ctaSub:      "We are always open to individuals dedicated to social impact.",
    ctaBtn:      "Contact Us",
  },
  ID: {
    label:       "Tentang Kami",
    headline:    "Kami ingin membangun dunia di mana organisasi dijalankan dengan tanggung jawab sosial dan didorong oleh tujuan & dampak",
    stat1l: "Petani Terdampak", stat2l: "Kabupaten/Kota", stat3l: "Penerima Manfaat",
    eyebrow:     "Orang-orang di Balik Misi",
    title:       "Tim Kami",
    subtitle:    "Dipimpin oleh individu-individu berpengalaman yang berkomitmen penuh terhadap ketahanan pangan dan pemberdayaan masyarakat Aceh.",
    viewProfile: "Lihat Profil",
    cta:         "Ingin bergabung bersama kami?",
    ctaSub:      "Kami selalu terbuka untuk individu yang berdedikasi terhadap dampak sosial.",
    ctaBtn:      "Hubungi Kami",
  },
};

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.1) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// ─── Hero Section ──────────────────────────────────────────────────────────────
// ─── Hero Section ──────────────────────────────────────────────────────────────
const Hero = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full min-h-[100vh] overflow-hidden flex flex-col">
      <img
        src="https://i.ibb.co.com/fzttbmdt/0a61a15b-f7d0-4700-a866-66d5dd91f9c4.jpg"
        alt="Tim Rumoh Pangan Aceh"
        className="absolute inset-0 w-full h-full object-cover object-center scale-105"
        style={{
          transition: "transform 8s ease-out",
          transform: loaded ? "scale(1)" : "scale(1.05)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d0c0a]/90 via-[#1a1a18]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d0c0a]/80 via-transparent to-transparent" />
      <div
        className="absolute left-0 top-0 h-full w-1 bg-[#c9862a]"
        style={{
          transform: loaded ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "top",
          transition: "transform 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s",
        }}
      />

      <div className="relative z-10 flex-1 flex flex-col justify-end pb-16 md:pb-28 px-5 md:px-16 lg:px-24">
        <div
          className="flex items-center gap-2 md:gap-3 mb-4 md:mb-8"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.8s ease 0.4s",
          }}
        >
          <div className="h-px w-8 md:w-12 bg-[#c9862a]" />
          <span className="text-[#c9862a] text-[10px] md:text-xs font-bold tracking-[3px] md:tracking-[4px] uppercase">
            {tx.label}
          </span>
        </div>

        <h1
          className="font-serif text-[26px] leading-[1.25] md:text-6xl lg:text-7xl md:leading-[1.05] text-white max-w-5xl"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(30px)",
            transition: "all 1s ease 0.6s",
          }}
        >
          {tx.headline}
        </h1>

        <div
          className="mt-8 md:mt-16 flex flex-wrap gap-6 md:gap-10 border-t border-white/10 pt-5 md:pt-8"
          style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.8s ease 1s" }}
        >
          {[
            { n: "200+", l: tx.stat1l },
            { n: "6", l: tx.stat2l },
            { n: "6000+",   l: tx.stat3l },
          ].map(({ n, l }) => (
            <div key={l} className="flex flex-col gap-1">
              <span className="font-serif text-xl md:text-4xl text-[#c9862a] font-bold">{n}</span>
              <span className="text-white/50 text-[9px] md:text-xs font-bold tracking-[1.5px] md:tracking-[2px] uppercase">{l}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 right-6 md:right-16 z-10 flex-col items-center gap-2 hidden md:flex">
        <span className="text-white/30 text-[10px] tracking-[3px] uppercase rotate-90 origin-center mb-4">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
};

// ─── Team List Item ────────────────────────────────────────────────────────────
const TeamListItem = ({ member, idx, viewProfile }) => {
  const ref = useRef(null);
  const inView = useInView(ref, 0.1);

  return (
    <div
      ref={ref}
      className="py-12 md:py-16"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: "all 0.8s ease",
      }}
    >
      {/* Name + Role heading */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-baseline gap-3 md:gap-4 flex-wrap">
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1a1a18] leading-tight">
            {member.nama}
          </h2>
          <span className="text-[#c9862a] text-[10px] md:text-sm font-bold tracking-[1.5px] md:tracking-[2px] uppercase border border-[#c9862a]/40 px-2 py-0.5 md:px-3 md:py-1">
            {member.role}
          </span>
        </div>
        {/* Gold underline accent */}
        <div className="mt-3 flex items-center gap-3">
          <div className="w-12 md:w-16 h-[2px] bg-[#c9862a]" />
          <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
          <div className="flex-1 h-px bg-[#e8e0d0]" />
        </div>
      </div>

      {/* Photo + Description row */}
      <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">

        {/* Photo */}
        <div className="flex-shrink-0 w-full md:w-[280px] lg:w-[320px]">
          <div className="relative overflow-hidden bg-[#e8e0d0]" style={{ aspectRatio: "3/4" }}>
            <img
              src={member.url}
              alt={member.nama}
              className="w-full h-full object-cover object-top"
            />
            {/* Gold bottom bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#c9862a]" />
          </div>

          {/* LinkedIn below photo */}
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center gap-2 text-[#3a3a34] hover:text-[#c9862a] text-xs font-bold tracking-widest uppercase transition-colors duration-200 group/link"
            >
              <FaLinkedin size={16} />
              <span className="border-b border-transparent group-hover/link:border-[#c9862a] transition-all pb-px">
                {viewProfile}
              </span>
              <HiArrowRight size={12} className="transition-transform duration-200 group-hover/link:translate-x-1" />
            </a>
          )}
        </div>

        {/* Description */}
        <div className="flex-1 min-w-0">
          {/* Split deskripsi into paragraphs by \r\n\r\n */}
          {member.deskripsi.split(/\r?\n\r?\n/).map((para, i) => (
            <p
              key={i}
              className="text-[#4a4a44] text-[15px] leading-[1.9] text-justify mb-5 last:mb-0"
            >
              {para.trim()}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Team Section ──────────────────────────────────────────────────────────────
const TeamSection = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const [rawMembers, setRawMembers] = useState([]);

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const apiUrl = process.env.REACT_APP_URL_API;
        const res = await axios.get(`${apiUrl}/all-team`);
        setRawMembers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching member:", err);
      }
    };
    fetchMember();
  }, []);

  const { translated: teamMembers, loading } = useTranslateItems(
    rawMembers,
    language,
    ["role", "deskripsi"]
  );

  return (
    <section className="bg-[#f7f3ec] py-24 px-8 md:px-8 relative overflow-hidden">
      {/* Watermark */}
      <div className="absolute top-8 right-0 font-serif text-[100px] md:text-[220px] font-bold text-[#e8e0d0] leading-none select-none pointer-events-none overflow-hidden opacity-60">
        TIM
      </div>

      <div className="container md:px-24 mx-auto relative z-10">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-12 gap-4 md:gap-6">
          <div>
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
              <div className="h-px w-8 md:w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-[10px] md:text-xs font-bold tracking-[2px] md:tracking-[4px] uppercase">
                {tx.eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-6xl font-bold text-[#1a1a18] leading-tight">
              {tx.title}
            </h2>
          </div>
          <p className="text-[#6b6456] text-sm md:text-base leading-relaxed max-w-sm md:text-right">
            {tx.subtitle}
          </p>
        </div>

        {/* Loading skeleton */}
        {loading ? (
          <div className="space-y-16">
            {rawMembers.map((_, i) => (
              <div key={i} className="animate-pulse py-12 border-b border-[#e8e0d0]">
                <div className="h-8 bg-[#e8e0d0] rounded w-1/3 mb-8" />
                <div className="flex gap-10">
                  <div className="w-[280px] flex-shrink-0 bg-[#e8e0d0] rounded" style={{ aspectRatio: "3/4" }} />
                  <div className="flex-1 space-y-3 pt-2">
                    <div className="h-3 bg-[#e8e0d0] rounded w-full" />
                    <div className="h-3 bg-[#e8e0d0] rounded w-5/6" />
                    <div className="h-3 bg-[#e8e0d0] rounded w-full" />
                    <div className="h-3 bg-[#e8e0d0] rounded w-4/6" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-[#e8e0d0]">
            {teamMembers.map((member, idx) => (
              <TeamListItem
                key={member.id}
                member={member}
                idx={idx}
                viewProfile={tx.viewProfile}
              />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 border-t border-[#e8e0d0] pt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="font-serif text-2xl font-bold text-[#1a1a18] mb-1">{tx.cta}</p>
            <p className="text-[#a09880] text-sm">{tx.ctaSub}</p>
          </div>
          <button
            onClick={() =>
              window.open(
                "https://api.whatsapp.com/send/?phone=6285385482041&text&type=phone_number&app_absent=0",
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="flex items-center gap-3 bg-[#1a1a18] hover:bg-[#c9862a] text-white text-sm font-bold tracking-widest uppercase px-8 py-4 transition-all duration-300 whitespace-nowrap group/btn">
            {tx.ctaBtn}
            <HiArrowRight className="transition-transform duration-300 group-hover/btn:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const TeamPage = () => (
  <main className="overflow-x-hidden font-homepage">
    <Navbar />
    <section id="hero">
      <Hero />
    </section>
    <section id="team">
      <TeamSection />
    </section>
    <section id="footer">
      <Footer />
    </section>
  </main>
);

export default TeamPage;